<template>
	<div class="document-detail min-h-full bg-white">
		<div class="flex flex-col gap-2 p-4 bg-white [overflow-wrap:anywhere]">
			<div>
				<div class="flex justify-between items-start gap-2">
					<h1 class="text-lg font-medium min-w-0">
						{{ splitHdnValue(selectedDocument.documentType)[1] }}
					</h1>
					<div class="flex shrink-0 items-center">
						<button
							type="button"
							class="rounded border px-2 py-1 text-sm"
							@click="emit('toggleExpand')"
						>
							{{ expanded ? "Verkleinen" : "Vergroten" }}
						</button>
						<AppButton
							variant="ghost"
							icon="close"
							class="pt-0.5"
							aria-label="Sluiten"
							@click="emit('close')"
						/>
					</div>
				</div>
				<p
					v-if="selectedDocument.documentTypeDescription"
					class="text-sm text-gray-500"
				>
					{{ selectedDocument.documentTypeDescription }}
				</p>
			</div>

			<div
				v-if="selectedDocument.partijNawData"
				class="flex items-center gap-1 row"
			>
				<span class="ht-icon person-icon text-2xl"> person </span>
				<span class="text-sm font-medium">
					{{
						selectedDocument.partijNawData.correspondenceFirstname ??
						selectedDocument.partijNawData.initials ??
						selectedDocument.partijNawData.givenNames
					}}
					{{
						selectedDocument.partijNawData.correspondenceInsert ??
						selectedDocument.partijNawData.birthInsert
					}}
					{{
						selectedDocument.partijNawData.correspondenceSurname ??
						selectedDocument.partijNawData.birthSurname
					}}
				</span>
			</div>

			<p v-else class="text-sm text-gray-500">Geen specifieke klant</p>
		</div>
		<div class="coupling-layout" :class="{ 'has-preview': previewDocument }">
			<div
				class="min-w-0 flex flex-col gap-2 p-4 pt-0 [overflow-wrap:anywhere]"
			>
				<div class="flex flex-col gap-1 mb-3" :class="iconColor">
					<div class="flex items-center gap-1">
						<AppIcon :name="iconName" class="text-2xl" />
						<span class="font-semibold text-sm">
							{{
								documentStatus
									? splitHdnValue(documentStatus.status)[1]
									: "Geen status gevonden"
							}}
						</span>
					</div>
					<span v-if="documentStatus?.description" class="text-sm pl-7">
						{{ documentStatus.description }}
					</span>
				</div>

				<div
					v-if="selectedDocument.statusmelding"
					class="flex flex-col gap-1 p-3 mb-3 border rounded-sm"
					:class="statusmeldingColor"
				>
					<span class="text-sm font-semibold">
						{{ splitHdnValue(selectedDocument.statusmelding)[1] }}
					</span>
					<span
						v-if="selectedDocument.statusmeldingDescription"
						class="text-sm whitespace-pre-wrap"
					>
						{{ selectedDocument.statusmeldingDescription }}
					</span>
					<span v-if="selectedDocument.statusmeldingDatum" class="text-xs">
						{{ formatDate(new Date(selectedDocument.statusmeldingDatum)) }}
					</span>
				</div>

				<div v-if="linkedDocument" class="flex flex-col gap-2">
					<h2 class="font-medium">Gekoppeld bestand:</h2>
					<div
						class="flex items-center justify-between h-16 pl-4 pr-2 border rounded-sm group bg-gray-50"
					>
						<div class="flex gap-2 items-center">
							<img
								class="w-6 h-6"
								:src="getFileIcon(getFileType(linkedDocument.contentType))"
							/>
							<div class="flex flex-col">
								<span class="text-sm">{{ linkedDocument.filename }}</span>
								<span
									v-if="classification"
									class="text-xs"
									:class="
										classification.confidence >= CLASSIFICATIE_HOGE_CONFIDENCE
											? 'classification-high'
											: classification.confidence >=
												  CLASSIFICATIE_MIN_CONFIDENCE
												? 'classification-medium'
												: 'classification-unknown'
									"
									:title="`${Math.round(classification.confidence * 100)}% zekerheid`"
								>
									{{
										classification.confidence >= CLASSIFICATIE_MIN_CONFIDENCE
											? classification.label
											: CLASSIFICATIE_ONBEKEND_LABEL
									}}
								</span>
							</div>
						</div>

						<AppButton
							variant="ghost"
							icon="download"
							class="text-gray-400 opacity-0 group-hover:opacity-100 hover:text-gray-500"
							aria-label="Document downloaden"
							@click="downloadDocument"
						/>
					</div>
					<DocumentExtractionDetails
						v-if="extractionEnabled && linkedDocument"
						:document-id="linkedDocument.id"
						:classification-label="classification?.label"
					/>
					<div class="flex gap-2">
						<AppButton
							variant="primary"
							:icon="
								!canSendDocument && selectedDocument.sentDocumentAt
									? 'done'
									: 'send'
							"
							:disabled="!canSendDocument"
							@click="$emit('sendDocument')"
						>
							{{
								!canSendDocument
									? selectedDocument.sentDocumentAt
										? "Verstuurd"
										: "Versturen..."
									: "Versturen"
							}}
						</AppButton>
						<AppButton
							variant="secondary"
							icon="link_off"
							:disabled="!canConnectDocument"
							@click="emit('disconnectDocument')"
						>
							Ontkoppelen
						</AppButton>
					</div>
				</div>
				<div v-else-if="availableDocuments && canConnectDocument">
					<h2 class="font-medium">Koppel een bestand:</h2>
					<LoanApplicationDocumentsAvailableDocuments
						:available-documents
						:requested-document-type="selectedDocument.documentType"
						:partij-naw-data="selectedDocument.partijNawData"
						@select="(doc) => emit('connectDocument', doc)"
						@preview="(doc) => (previewDocumentId = doc.id)"
					/>
				</div>
				<div v-if="canCreateLetter">
					<AppButton
						variant="secondary"
						icon="draft"
						@click="showLetterDialog = true"
					>
						Maak brief
					</AppButton>
				</div>
				<div v-if="selectedDocument.opgevraagdViaBron">
					<h2 class="font-medium">Opgevraagd via externe bron</h2>
					<p class="text-sm">
						Dit document is opgevraagd via een externe bron, en kan niet
						handmatig worden aangeleverd.
					</p>
				</div>
			</div>

			<RequestedDocumentPreview
				v-if="previewDocument"
				:key="selectedDocument.id"
				:document="previewDocument"
				@close="previewDocumentId = null"
			/>
		</div>
		<GeneratedLetterDialog
			v-if="showLetterDialog"
			:requested-document-id="selectedDocument.id"
			:already-linked="hasLinkedDocument"
			@close="showLetterDialog = false"
		/>
	</div>
</template>

<script lang="ts" setup>
import { computed, ref, toRefs, watch } from "vue";
import { useLoanApplicationDocumentIcon } from "@/composables/useLoanApplicationDocumentIcon";
import { useUserStore } from "@/store/useUserStore";
import RequestedDocumentPreview from "./RequestedDocumentPreview.vue";
import LoanApplicationDocumentsAvailableDocuments from "./LoanApplicationDocumentsAvailableDocuments.vue";
import GeneratedLetterDialog from "./GeneratedLetterDialog.vue";
import { splitHdnValue } from "@/helpers/hdnHelpers";
import { getFileIcon, getFileType } from "@/helpers/fileHelpers";
import { formatDate } from "@/helpers/uiHelpers";
import { saveAs } from "file-saver";
import { storeToRefs } from "pinia";
import { useFileUploadStore } from "@/store/useFileUploadStore";
import {
	getStatus,
	type DossierDocument,
	type HdnRequestedDocument,
} from "@/composables/useLoanApplicationSortedDocuments";
import { useDossierDocumentUrlStore } from "@/store/useDossierDocumentUrlStore";
import { inject, type Ref } from "vue";
import {
	CLASSIFICATIE_MIN_CONFIDENCE,
	CLASSIFICATIE_HOGE_CONFIDENCE,
	CLASSIFICATIE_ONBEKEND_LABEL,
} from "@/parameters/documentClassificatie";
import DocumentExtractionDetails from "./DocumentExtractionDetails.vue";
import { useFeatureFlag } from "@/composables/useFeatureFlag";

const props = withDefaults(
	defineProps<{
		sendingDocument?: boolean;
		expanded?: boolean;
		selectedDocument: HdnRequestedDocument;
	}>(),
	{
		sendingDocument: false,
	},
);

const emit = defineEmits<{
	connectDocument: [doc: DossierDocument];
	sendDocument: [];
	disconnectDocument: [];
	close: [];
	toggleExpand: [];
}>();

const canSendDocument = computed(() => {
	return (
		(!selectedDocument.value.sentDocumentAction && !!linkedDocument.value) ||
		linkedDocument.value?.id !==
			selectedDocument.value.sentDocumentAction?.documentId
	);
});

const linkedDocument = computed<DossierDocument | null>(() => {
	if (!props.selectedDocument.linkedDocumentAction?.documentId) return null;

	return props.selectedDocument.linkedDocumentAction.document ?? null;
});

const classificationsMap =
	inject<Ref<Map<string, { label: string; confidence: number }>>>(
		"classificationsMap",
	);

const classification = computed(() => {
	const docId = linkedDocument.value?.id;
	if (!docId || !classificationsMap?.value) return null;
	return classificationsMap.value.get(docId) ?? null;
});

const extractionEnabled = useFeatureFlag("document-extraction-intermediary");

const fileUploadStore = useFileUploadStore();
const { existingFiles: availableDocuments } = storeToRefs(fileUploadStore);

const { selectedDocument } = toRefs(props);
const documentStatus = computed(() => {
	return getStatus(selectedDocument.value);
});
const { iconName, iconColor } = useLoanApplicationDocumentIcon(
	() => documentStatus.value?.status,
);
const statusmeldingColor = computed(() => {
	return selectedDocument.value.statusmelding?.startsWith("02 ")
		? "bg-gray-50 border-gray-200 text-gray-900"
		: "bg-red-50 border-red-200 text-red-900";
});

async function downloadDocument() {
	if (!linkedDocument.value) return;

	const fileUrl = await useDossierDocumentUrlStore().getFileUrl(
		linkedDocument.value.id,
	);

	const savedFileName = linkedDocument.value.filename;
	saveAs(fileUrl, savedFileName);
}

const connectableStatuses = [
	"01 Aan te leveren",
	"02 In behandeling",
	"03 Ontvangen",
	"07 Afgekeurd en opnieuw aan te leveren (zie toelichting)",
];

const canConnectDocument = computed(() => {
	if (selectedDocument.value.opgevraagdViaBron) {
		return false;
	}

	if (!documentStatus.value) {
		return true;
	}

	return connectableStatuses.includes(documentStatus.value.status);
});

const previewDocumentId = ref<string | null>(null);
const previewDocument = computed(() =>
	canConnectDocument.value &&
	!props.selectedDocument.linkedDocumentAction?.documentId
		? availableDocuments.value?.find(
				(doc) => doc.id === previewDocumentId.value,
			)
		: undefined,
);
watch(
	() => props.selectedDocument.id,
	() => {
		previewDocumentId.value = null;
	},
	{ flush: "sync" },
);
watch(previewDocument, (document) => {
	if (!document) previewDocumentId.value = null;
});

const showLetterDialog = ref(false);

const userStore = useUserStore();

// Of er al een document aan dit verzoek gekoppeld is. Bewust afgeleid van de
// action-documentId en niet van de geladen `linkedDocument`-relatie: die relatie
// kan (nog) null zijn terwijl de koppeling wél bestaat, waardoor de knop anders
// onterecht zou verschijnen en een tweede koppeling toegelaten zou worden.
const hasLinkedDocument = computed(
	() => !!selectedDocument.value.linkedDocumentAction?.documentId,
);

// "Maak brief" alleen voor adviseur/beheerder, wanneer koppelen mag, er nog
// niets gekoppeld is en het geen extern-brondocument is. De rol-check bepaalt
// de zichtbaarheid.
const canCreateLetter = computed(() => {
	const role = userStore.user?.role;
	if (role !== "intermediary" && role !== "admin") {
		return false;
	}

	return (
		!hasLinkedDocument.value &&
		canConnectDocument.value &&
		!selectedDocument.value.opgevraagdViaBron
	);
});
</script>

<style lang="css" scoped>
.document-detail {
	container-type: inline-size;
}
.coupling-layout {
	display: grid;
	grid-template-columns: minmax(0, 1fr);
}
@container (min-width: 700px) {
	.coupling-layout.has-preview {
		grid-template-columns: minmax(0, 1fr) minmax(0, 1.3fr);
	}
}
.v-enter-active,
.v-leave-active {
	transition: all 0.15s ease;
}

.v-enter-from,
.v-leave-to {
	opacity: 0;
}

.person-icon {
	color: var(--text-secondary);
}
</style>
